import { dist2, iatan2, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot, Enemy, Hero } from '../types.ts';
import { damageEnemy, damageHero, ev, isFlying } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The alarm bells (Las vías 2, docs/SAGAS.md): Dorn's guard automatons, bone and stone like the crypt's Custodian but
 * small, stand in three rooms round the node. They can't tell friend from foe: each strikes whatever comes nearest its
 * post, hero or nightmare. Heroes can't break them; nightmares are drawn to them and can, so leading the nightmares
 * into a room helps. A hero who stays by a room's alarm lever pulls it, and that room's automatons go still, and it takes a
 * while under their blows: it goes better with someone else (or a nightmare) nearer them meanwhile. Every lever down wins it; the node falling loses it.
 */

const NEVER = 0x7fffffff;

export const bellsTick = (s: BattleState): void => {
  const r = s.rules?.bells;
  if (!r) return;
  const st = (s.bells ??= wake(s));
  if (!s.started || st.done) return;
  const standing = s.heroes.filter((h) => h.downTicks === 0);
  r.rooms.forEach((room, i) => {
    if (st.down[i]) return;
    const by = standing.some((h) => withinRange(h.x, h.y, room.lever.x, room.lever.y, r.reach));
    st.held[i] = by ? (st.held[i] ?? 0) + 1 : 0;
    if (st.held[i] === 1) ev(s, { t: 'bells', kind: 'pull', room: i, x: room.lever.x, y: room.lever.y });
    if ((st.held[i] ?? 0) < r.pull) return;
    // Down: that room's bells stop, and its automatons go still where they stand.
    st.down[i] = true;
    for (const b of s.bots ?? []) {
      if (b.kind !== 'guard' || b.room !== i) continue;
      b.off = true;
      b.taunt = 0;
      b.anim = 'idle';
    }
    ev(s, { t: 'bells', kind: 'down', room: i, x: room.lever.x, y: room.lever.y });
  });
  if (st.down.every(Boolean)) {
    st.done = s.tick;
    ev(s, { t: 'bells', kind: 'all', room: -1, x: 0, y: 0 });
  }
};

/** The rooms' automatons, at their posts. */
const wake = (s: BattleState): NonNullable<BattleState['bells']> => {
  const r = s.rules?.bells;
  const rooms = r?.rooms ?? [];
  s.bots ??= [];
  rooms.forEach((room, i) => {
    for (const p of room.posts) {
      s.bots?.push({
        id: s.nextId++,
        owner: 0,
        pid: '',
        kind: 'guard',
        x: p.x,
        y: p.y,
        hp: r?.hp ?? 1,
        maxHp: r?.hp ?? 1,
        damage: r?.damage ?? 0,
        radius: r?.radius ?? 0,
        every: r?.every ?? 12,
        cd: 0,
        expires: NEVER,
        taunt: r?.taunt ?? 0,
        aura: 0,
        auraBp: 0,
        facing: iatan2(-p.y, -p.x),
        anim: 'idle',
        animT: 0,
        room: i,
        post: { x: p.x, y: p.y },
      });
    }
  });
  return { held: rooms.map(() => 0), down: rooms.map(() => false), done: 0 };
};

/**
 * A guard: after whatever is nearest it, hero or nightmare, as long as it stays near its post; on a nightmare it took on
 * until it falls or gets away; back to its post with nothing about.
 */
export const guardTick = (s: BattleState, b: Bot): void => {
  // Dorn's own (Las vías 4) are on the heroes' side: the same automatons, after nightmares only.
  const r = b.ally ? s.rules?.rising?.dorn : s.rules?.bells;
  if (!r || b.off || !b.post) return;
  if (b.cd > 0) b.cd--;
  let hero: Hero | null = null;
  let foe: Enemy | null = null;
  const held = b.prey ? s.enemies.find((e) => e.id === b.prey && e.hp > 0) : undefined;
  if (held && withinRange(held.x, held.y, b.post.x, b.post.y, r.aggro)) foe = held;
  const leash = r.aggro * r.aggro;
  let bd = foe ? -1 : 0x7fffffff;
  for (const h of b.ally ? [] : s.heroes) {
    if (h.downTicks > 0 || (h.hidden ?? 0) > 0 || dist2(h.x, h.y, b.post.x, b.post.y) > leash) continue;
    const d = dist2(h.x, h.y, b.x, b.y);
    if (d > bd) continue;
    bd = d;
    hero = h;
  }
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.kind === 'boneWall' || isFlying(e) || dist2(e.x, e.y, b.post.x, b.post.y) > leash) continue;
    const d = dist2(e.x, e.y, b.x, b.y);
    if (d >= bd) continue;
    bd = d;
    foe = e;
    hero = null;
  }
  if (foe) b.prey = foe.id;
  else delete b.prey;
  const t = foe ?? hero;
  if (!t) {
    if (!withinRange(b.x, b.y, b.post.x, b.post.y, 200)) {
      b.anim = 'walk';
      b.facing = iatan2(b.post.y - b.y, b.post.x - b.x);
      stepToward(b, b.post.x, b.post.y, r.step);
    } else if (b.anim !== 'attack' || b.animT > 6) b.anim = 'idle';
    return;
  }
  b.facing = iatan2(t.y - b.y, t.x - b.x);
  if (!withinRange(b.x, b.y, t.x, t.y, b.radius)) {
    b.anim = 'walk';
    stepToward(b, t.x, t.y, r.step);
    return;
  }
  if (b.cd > 0) {
    if (b.anim === 'attack' && b.animT > 6) b.anim = 'idle';
    return;
  }
  b.cd = b.every;
  b.anim = 'attack';
  b.animT = 0;
  if (foe) damageEnemy(s, foe, r.foe, { pid: '', noReact: true });
  else if (hero) damageHero(s, hero, b.damage, null);
};


/** Every lever down: won (a moment later); the node falling: lost. */
export const bellsResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.bells;
  if (!r) return null;
  if (s.keep.hp <= 0) return 'lost';
  return s.bells?.done && s.tick - s.bells.done >= r.beat ? 'won' : null;
};
